import { apiService } from './apiService';
import { ActivityType } from '../models/diary';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://api.eternal-health.com/api';

export type IntensityLevel = 'low' | 'mid' | 'high';

// One configured intensity level for an activity type.
export interface ActivityCatalogLevel {
  id: number;
  intensityLevel: IntensityLevel;
  intensityLevelDisplay: string;
  kcalPerKgMin: number;
  carbsPct: number;
  carbsPercentageDisplay: string;
  // The API echoes the nested activityType inside each level (redundant) — safe to ignore.
  activityType?: ActivityType;
}

// An activity type grouped with its 3 intensity levels (null until configured).
export interface ActivityCatalogEntry {
  activityType: ActivityType;
  levels: {
    low: ActivityCatalogLevel | null;
    mid: ActivityCatalogLevel | null;
    high: ActivityCatalogLevel | null;
  };
}

// Values to set for a single level. Both fields required together.
export interface ActivityLevelInput {
  kcalPerKgMin: number;
  carbsPct: number;
}

export interface CreateActivityTypeData {
  name: string;
  description?: string | null;
}

export const activityTypeService = {
  getAll: async (): Promise<ActivityType[]> => {
    const url = `${API_BASE_URL}/clinic/activity-types/`;
    try {
      return await apiService.get(url);
    } catch (error) {
      console.error('Error fetching activity types:', error);
      throw error;
    }
  },

  create: async (data: CreateActivityTypeData): Promise<ActivityType> => {
    const url = `${API_BASE_URL}/clinic/activity-types/`;
    try {
      return await apiService.post(url, data);
    } catch (error) {
      console.error('Error creating activity type:', error);
      throw error;
    }
  },

  update: async (id: number, data: Partial<CreateActivityTypeData>): Promise<ActivityType> => {
    const url = `${API_BASE_URL}/clinic/activity-types/${id}/`;
    try {
      return await apiService.patch(url, data);
    } catch (error) {
      console.error(`Error updating activity type ${id}:`, error);
      throw error;
    }
  },

  // 204 if removable; 409 with { error } if a patient Activity references it.
  remove: async (id: number): Promise<void> => {
    const url = `${API_BASE_URL}/clinic/activity-types/${id}/`;
    try {
      await apiService.delete(url);
    } catch (error) {
      console.error(`Error deleting activity type ${id}:`, error);
      throw error;
    }
  },
};

export const activityCatalogService = {
  // All activity types grouped with their (possibly null) intensity levels.
  getAll: async (): Promise<ActivityCatalogEntry[]> => {
    const url = `${API_BASE_URL}/clinic/activity-catalog/`;
    try {
      const response = await apiService.get(url);
      return response.results || [];
    } catch (error) {
      console.error('Error fetching activity catalog:', error);
      throw error;
    }
  },

  // Configure (create/update) one or more levels for a type. Send only the levels to set.
  configure: async (
    activityTypeId: number,
    levels: Partial<Record<IntensityLevel, ActivityLevelInput>>,
  ): Promise<ActivityCatalogEntry> => {
    const url = `${API_BASE_URL}/clinic/activity-catalog/`;
    try {
      return await apiService.post(url, { activityTypeId, levels });
    } catch (error) {
      console.error('Error configuring activity catalog:', error);
      throw error;
    }
  },

  // Remove all level config for a type (the ActivityType itself remains).
  clear: async (activityTypeId: number): Promise<void> => {
    const url = `${API_BASE_URL}/clinic/activity-catalog/${activityTypeId}/`;
    try {
      await apiService.delete(url);
    } catch (error) {
      console.error(`Error clearing activity catalog ${activityTypeId}:`, error);
      throw error;
    }
  },
};
